import React, { forwardRef } from 'react';
import type { CreateProps } from '@atlaskit/atlassian-navigation';
import { SplitButton } from '@atlaskit/button/new';
import { Box } from '@atlaskit/primitives/compiled';
import DropdownMenu from '@atlaskit/dropdown-menu';
import { CreateButton as Nav4CreateButton } from '@atlaskit/navigation-system';
import { fg } from '@atlassian/jira-feature-gating';
import { GLOBAL_NAV_CREATE_BUTTON_SPOTLIGHT_ID } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/constants.tsx';
import { SpotlightTarget } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/spotlight-target/index.tsx';
import { CreateWithAIDropdown } from '@atlassian/jira-smart-creation';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useOrgId } from '@atlassian/jira-tenant-context-controller/src/components/org-id/index.tsx';
import { useRovoEntitlementResource } from '@atlassian/jira-router-resources-rovo-entitlement/src/services/index.tsx';
import { GLOBAL_CREATE_BUTTON_NAV4, GLOBAL_CREATE_SPLIT_BUTTON } from './constants.tsx';

export const Create = forwardRef<HTMLButtonElement, CreateProps>(
	(props, ref): React.JSX.Element => {
		const cloudId = useCloudId();
		const accountId = useAccountId() ?? '';
		const orgId = useOrgId() ?? '';
		const { data } = useRovoEntitlementResource();
		const shouldShowCreateWithAiDropdown =
			(data?.isRovoLLMEnabled && fg('jira_aifc_enabled')) ?? false;

		// This feature gate disables the server-specific code path, aligning SSR and CSR content for hydration
		if (__SERVER__ && !fg('jfp-magma-4013-fix-top-nav-ssr-hydration')) {
			if (shouldShowCreateWithAiDropdown) {
				return (
					<SplitButton appearance="primary">
						<Nav4CreateButton ref={ref} testId={GLOBAL_CREATE_SPLIT_BUTTON}>
							{props.text}
						</Nav4CreateButton>
						<DropdownMenu shouldRenderToParent />
					</SplitButton>
				);
			}

			return (
				<Nav4CreateButton ref={ref} testId={GLOBAL_CREATE_BUTTON_NAV4}>
					{props.text}
				</Nav4CreateButton>
			);
		}
		const { onClick, text } = props;

		if (shouldShowCreateWithAiDropdown) {
			return (
				<SpotlightTarget name={GLOBAL_NAV_CREATE_BUTTON_SPOTLIGHT_ID}>
					<Box>
						<SplitButton appearance="primary">
							<Nav4CreateButton ref={ref} onClick={onClick} testId={GLOBAL_CREATE_SPLIT_BUTTON}>
								{text}
							</Nav4CreateButton>
							<CreateWithAIDropdown cloudId={cloudId} accountId={accountId} orgId={orgId} />
						</SplitButton>
					</Box>
				</SpotlightTarget>
			);
		}

		return (
			<SpotlightTarget name={GLOBAL_NAV_CREATE_BUTTON_SPOTLIGHT_ID}>
				<Box>
					<Nav4CreateButton ref={ref} onClick={onClick} testId={GLOBAL_CREATE_BUTTON_NAV4}>
						{text}
					</Nav4CreateButton>
				</Box>
			</SpotlightTarget>
		);
	},
);
